/* FOM · paleta de comandos e busca global (Ctrl+K).

   Uma unica porta para TUDO: nos, conhecimento, lacunas, investigacoes e
   acoes. O usuario digita o que lembra — "comercial", "INV-004", "lacuna" —
   e chega la sem precisar saber em qual camada a informacao mora.

   O que ela NAO e:
     · nao e um backend novo — a busca roda sobre o que o mapa ja carregou
       (nos com know/gaps vem inteiros no GET), e investigacoes vem da rota
       que ja existe, carregadas uma vez por abertura;
     · nao e um caminho novo de escrita — toda acao executa exatamente o
       mesmo codigo dos botoes que ja existiam. */
import type { FomNode, InvestigationResumo } from '../core/types.ts';

export type AcaoPaleta = {
  id: string;
  rotulo: string;
  dica?: string;
  executar: () => void;
};

export type PaletaOpcoes = {
  nos: () => FomNode[];
  acoes: () => AcaoPaleta[];
  /* lazy e cacheado por abertura: a lista so e pedida se a paleta abrir */
  investigacoes: () => Promise<InvestigationResumo[]>;
  aoIrParaNo: (externalId: string) => void;
  aoInvestigarLacuna: (no: FomNode, lacuna: string) => void;
  aoAbrirInvestigacao: (inv: InvestigationResumo) => void;
};

type Resultado = {
  grupo: 'Acoes' | 'Nos' | 'Conhecimento' | 'Lacunas' | 'Investigacoes';
  titulo: string;
  detalhe: string;
  executar: () => void;
};

/* acentos fora da comparacao: "operacao" encontra "Operação" */
function chave(s: string): string {
  return s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

const LIMITE_POR_GRUPO = 5;

export function montarPaleta(host: HTMLElement, o: PaletaOpcoes) {
  host.className = 'paleta-host';
  host.hidden = true;
  host.innerHTML = `
    <div class="paleta" role="dialog" aria-modal="true" aria-label="Buscar e executar">
      <div class="paleta-topo">
        <span class="paleta-lupa" aria-hidden="true">⌕</span>
        <input class="paleta-entrada" type="text"
               placeholder="Buscar no, conhecimento, lacuna, investigacao… ou executar uma acao"
               aria-label="Buscar" autocomplete="off" spellcheck="false" />
        <kbd class="paleta-kbd">Esc</kbd>
      </div>
      <ul class="paleta-resultados" role="listbox"></ul>
      <div class="paleta-rodape">
        <span><kbd>↑</kbd><kbd>↓</kbd> navegar</span>
        <span><kbd>Enter</kbd> abrir</span>
        <span><kbd>Ctrl</kbd><kbd>K</kbd> abrir/fechar</span>
      </div>
    </div>`;

  const entrada = host.querySelector<HTMLInputElement>('.paleta-entrada')!;
  const lista = host.querySelector<HTMLUListElement>('.paleta-resultados')!;

  let resultados: Resultado[] = [];
  let ativo = 0;
  let invsCache: InvestigationResumo[] | null = null;
  let carregandoInvs = false;

  function buscar(termo: string): Resultado[] {
    const t = chave(termo.trim());
    const saida: Resultado[] = [];
    const conta: Record<string, number> = {};
    const cabe = (r: Resultado): void => {
      conta[r.grupo] = (conta[r.grupo] ?? 0) + 1;
      if (conta[r.grupo]! <= LIMITE_POR_GRUPO) saida.push(r);
    };

    /* acoes primeiro: com a paleta vazia, elas SAO o menu */
    for (const a of o.acoes()) {
      if (!t || chave(a.rotulo).includes(t)) {
        cabe({ grupo: 'Acoes', titulo: a.rotulo, detalhe: a.dica ?? '', executar: a.executar });
      }
    }
    if (!t) return saida;

    for (const n of o.nos()) {
      const alvo = chave(`${n.name} ${n.code ?? ''} ${n.external_id}`);
      if (alvo.includes(t)) {
        cabe({
          grupo: 'Nos',
          titulo: n.name,
          detalhe: `${n.code ?? n.external_id} · ${n.st}` + (n.origin === 'user' ? ' · USER' : ''),
          executar: () => o.aoIrParaNo(n.external_id),
        });
      }
      for (const k of n.know) {
        if (chave(k.text).includes(t)) {
          cabe({
            grupo: 'Conhecimento',
            titulo: k.text.length > 70 ? k.text.slice(0, 70) + '…' : k.text,
            detalhe: `${k.st} · em "${n.name}"`,
            executar: () => o.aoIrParaNo(n.external_id),
          });
        }
      }
      for (const g of n.gaps) {
        if (chave(g).includes(t)) {
          cabe({
            grupo: 'Lacunas',
            titulo: g.length > 70 ? g.slice(0, 70) + '…' : g,
            detalhe: `em "${n.name}" · Enter investiga`,
            executar: () => o.aoInvestigarLacuna(n, g),
          });
        }
      }
    }

    for (const inv of invsCache ?? []) {
      if (chave(inv.code + ' ' + inv.question).includes(t)) {
        cabe({
          grupo: 'Investigacoes',
          titulo: `${inv.code} — ${inv.question.length > 60 ? inv.question.slice(0, 60) + '…' : inv.question}`,
          detalhe: inv.status,
          executar: () => o.aoAbrirInvestigacao(inv),
        });
      }
    }
    /* grupos CONTIGUOS: os resultados chegam intercalados (a varredura e
       por no), e cabecalho de grupo repetido le como bug. Ordena por grupo,
       preservando a ordem interna de cada um. */
    const ORDEM = { Acoes: 0, Nos: 1, Conhecimento: 2, Lacunas: 3, Investigacoes: 4 };
    return saida
      .map((r, i) => ({ r, i }))
      .sort((a, b) => (ORDEM[a.r.grupo] - ORDEM[b.r.grupo]) || (a.i - b.i))
      .map((x) => x.r);
  }

  function pintar(): void {
    lista.textContent = '';
    if (!resultados.length) {
      const li = document.createElement('li');
      li.className = 'paleta-vazio';
      li.textContent = entrada.value.trim()
        ? 'Nada encontrado. A busca cobre nos, conhecimento, lacunas, investigacoes e acoes.'
        : '';
      lista.appendChild(li);
      return;
    }
    let grupoAnterior = '';
    resultados.forEach((r, i) => {
      if (r.grupo !== grupoAnterior) {
        grupoAnterior = r.grupo;
        const cab = document.createElement('li');
        cab.className = 'paleta-grupo';
        cab.textContent = r.grupo === 'Nos' ? 'Nós'
          : r.grupo === 'Acoes' ? 'Ações'
          : r.grupo === 'Investigacoes' ? 'Investigações' : r.grupo;
        lista.appendChild(cab);
      }
      const li = document.createElement('li');
      li.className = 'paleta-item' + (i === ativo ? ' paleta-item-ativo' : '');
      li.setAttribute('role', 'option');
      li.setAttribute('aria-selected', String(i === ativo));
      li.innerHTML = `<span class="paleta-titulo"></span><span class="paleta-detalhe"></span>`;
      li.querySelector('.paleta-titulo')!.textContent = r.titulo;
      li.querySelector('.paleta-detalhe')!.textContent = r.detalhe;
      li.addEventListener('click', () => { fechar(); r.executar(); });
      li.addEventListener('mousemove', () => {
        if (ativo !== i) { ativo = i; pintar(); }
      });
      lista.appendChild(li);
    });
    lista.querySelector('.paleta-item-ativo')?.scrollIntoView?.({ block: 'nearest' });
  }

  function atualizar(): void {
    resultados = buscar(entrada.value);
    ativo = 0;
    pintar();
  }

  function abrir(): void {
    host.hidden = false;
    entrada.value = '';
    invsCache = null;
    atualizar();
    entrada.focus();
    if (!carregandoInvs) {
      carregandoInvs = true;
      void o.investigacoes()
        .then((invs) => { invsCache = invs; atualizar(); })
        .catch(() => { /* sem investigacoes na busca; o resto continua */ })
        .finally(() => { carregandoInvs = false; });
    }
  }

  function fechar(): void {
    host.hidden = true;
  }

  entrada.addEventListener('input', atualizar);
  entrada.addEventListener('keydown', (ev) => {
    if (ev.key === 'ArrowDown') {
      ev.preventDefault();
      ativo = Math.min(ativo + 1, resultados.length - 1);
      pintar();
    } else if (ev.key === 'ArrowUp') {
      ev.preventDefault();
      ativo = Math.max(ativo - 1, 0);
      pintar();
    } else if (ev.key === 'Enter') {
      ev.preventDefault();
      const r = resultados[ativo];
      if (r) { fechar(); r.executar(); }
    } else if (ev.key === 'Escape') {
      ev.preventDefault();
      /* CORRECAO 0081: a paleta CONSOME o Esc. Sem isto o evento subia para o
         handler do documento, que ja tinha visto a paleta fechar e seguia para
         o proximo da fila — um unico Esc fechava a paleta E derrubava a
         perspectiva Conhecimento junto. */
      ev.stopPropagation();
      fechar();
    }
  });
  /* clicar fora fecha; clicar dentro nao */
  host.addEventListener('mousedown', (ev) => {
    if (ev.target === host) fechar();
  });

  return {
    abrir,
    fechar,
    alternar(): void { if (host.hidden) abrir(); else fechar(); },
    get aberta(): boolean { return !host.hidden; },
  };
}

export type Paleta = ReturnType<typeof montarPaleta>;
